import { ArrowsPointingOutIcon } from '@heroicons/react/24/solid';
import React, { useState, useCallback, useEffect } from 'react';
import { Unity, useUnityContext } from 'react-unity-webgl';
import MusicTablet from '../../assets/images/mussila_music_tablet.webp';
import WPTablet from '../../assets/images/mussila_wordplay_tablet.webp';
import GiftWithBell from '../../assets/images/Callout-badge-calendar.webp';
import TertiaryButton from '../../components/buttons/TertiaryButton';
import Snowfall from 'react-snowfall';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Krans from '../../assets/images/xmas-krans.svg';
import { amplitudeTrack } from '../../services/amplitude';
import SocialShareButtons from '../../components/buttons/SocialShareButtons';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
// import ShowcaseApps from '../../components/ShowcaseApps';
import { isMobileSafari, isIPhone13, isIPad13 } from 'react-device-detect';
import useScript from '../../hooks/useScript';

const Calendar = () => {
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [seconds, setSeconds] = useState<number>(60);
	useScript('games/calendarISL/Base64Binary.js');
	useScript('games/calendarISL/MIDI.js');
	useScript('games/calendarISL/SS_MIDI.js');

	const {
		unityProvider,
		isLoaded,
		loadingProgression,
		requestFullscreen,
		sendMessage,
		// addEventListener,
		// removeEventListener,
	} = useUnityContext({
		loaderUrl: 'games/calendarISL/Build/Calendar.loader.js',
		dataUrl: 'games/calendarISL/Build/Calendar.data',
		frameworkUrl: 'games/calendarISL/Build/Calendar.framework.js',
		codeUrl: 'games/calendarISL/Build/Calendar.wasm',
	});

	const loadingPercentage = Math.round(loadingProgression * 100);

	// get screen resolution
	const [screenWidth, setScreenWidth] = useState<number>();
	const [screenHeight, setScreenHeight] = useState<number>();
	const [amplitudeSent, setAmplitudeSent] = useState<boolean>(false);

	const unlockGame = useCallback(() => {
		// console.log({ isLoaded }, 'unlockGame Called');
		const valid = [
			'1gXKvHWG05',
			'T2x4w6Evkg',
			'cAjAaDJ5gn',
			's6M1AYKk4b',
			'bBvXB30Uyu',
			'iGJX4pNkWx',
			'NplP7DhuiD',
			'AJFiHdBK81',
			'ZYTRFYAlo7',
			'K3XEP3OsjF',
		];
		const id = searchParams.get('id');
		if (isLoaded && id && valid.includes(id)) {
			// console.log('sending message to unity');
			sendMessage('Gifts_Manager_Date_Overwrite', 'OverwriteDate', 31);
		}
	}, [isLoaded, sendMessage, searchParams]);

	const handleResize = () => {
		// calculate 16x10 aspect ratio
		if (window.outerWidth < 1024) {
			setScreenWidth(Math.floor(window.outerWidth * 0.9));
			setScreenHeight(Math.floor(window.outerWidth * 0.9 * 0.625));
		} else if (window.outerWidth < 500) {
			// Minimum width for Unity to work...
			setScreenWidth(Math.floor(window.outerWidth * 0.8));
			setScreenHeight(Math.floor(window.outerWidth * 0.8 * 0.625));
		} else if (window.outerWidth > 1600) {
			setScreenWidth(Math.floor(window.outerWidth * 0.5));
			setScreenHeight(Math.floor(window.outerWidth * 0.5 * 0.625));
		} else {
			setScreenWidth(Math.floor(window.outerWidth * 0.75));
			setScreenHeight(Math.floor(window.outerWidth * 0.75 * 0.625));
		}
	};

	// We'll use a state to store the device pixel ratio.
	const [devicePixelRatio, setDevicePixelRatio] = useState(window.devicePixelRatio);

	const handleChangePixelRatio = useCallback(() => {
		// A function which will update the device pixel ratio of the Unity
		// Application to match the device pixel ratio of the browser.
		const updateDevicePixelRatio = function () {
			setDevicePixelRatio(window.devicePixelRatio);
		};
		// A media matcher which watches for changes in the device pixel ratio.
		const mediaMatcher = window.matchMedia(`screen and (resolution: ${devicePixelRatio}dppx)`);
		// Adding an event listener to the media matcher which will update the
		// device pixel ratio of the Unity Application when the device pixel
		// ratio changes.
		mediaMatcher.addEventListener('change', updateDevicePixelRatio);
		return () => {
			// Removing the event listener when the component unmounts.
			mediaMatcher.removeEventListener('change', updateDevicePixelRatio);
		};
	}, [devicePixelRatio]);

	const trackCalendarPageView = () => {
		amplitudeTrack('Calendar Icelandic Page View');
		setAmplitudeSent(true);
	};

	useEffect(() => {
		if (!amplitudeSent) {
			trackCalendarPageView();
		}

		handleChangePixelRatio();
		if (!screenWidth && !screenHeight) {
			handleResize();
		}
		if (seconds <= 0 || !searchParams.get('id')) {
			// no longer need to call interval after 60 seconds, quit early
			return;
		}
		const interval = setInterval(() => {
			if (isLoaded) {
				setSeconds((prevSeconds) => prevSeconds - 1);
				unlockGame();
			}
		}, 1000);
		return () => {
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleChangePixelRatio, isLoaded, seconds]);

	const debounce = (fn: () => void, ms: number) => {
		let timer: any;
		return () => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				timer = null;
				fn();
			}, ms);
		};
	}

	useEffect(() => {
		const debouncedHandleResize = debounce(handleResize, 1000);
		window.addEventListener('resize', debouncedHandleResize);

		return () => {
			window.removeEventListener('resize', debouncedHandleResize);
		}
	}, []);

	// If the last character of the url is '/'
	if (location.pathname.match('/.*/$')) {
		return (
			<Navigate
				replace
				to={{
					pathname: location.pathname.replace(/\/+$/, ''),
					search: location.search,
				}}
			/>
		);
	}

	if (!isIPad13 && (isIPhone13 || isMobileSafari)) {
		return (
			<div className='flex flex-col min-h-screen h-full place-content-center place-items-center bg-dark-blue-400 py-16 px-4'>
				<Snowfall style={{ height: '100%' }} />
				<div className='flex flex-col place-content-center place-items-center text-white prose text-center text-xl'>
					Jóladagatalið er ekki stutt fyrir iOS símtæki.
				</div>
				<div className='flex flex-col place-content-center place-items-center text-white prose text-center text-xl'>
					Vinsamlegast náðu í Mussila Music eða Wordplay á AppStore.
				</div>
				<div className='flex flex-col sm:grid grid-cols-12 rounded-smd bg-white border-2 border-gray-100 w-full mt-16 place-content-center mx-8'>
					<div className='pb-4 pt-12 flex flex-col place-content-between place-items-center col-span-6 px-4 mx-2 text-lg p-16 text-gray-500'>
						<img src={MusicTablet} alt='Music Tablet' className='h-32 w-auto place-self-center object-contain' />
						<p className='flex flex-col px-8 max-w-md my-4 place-content-center place-items-center'>
							{/* Learn to play the piano, create your own compositions and master the fundamentals of music theory. */}
							Náðu tökum á undirstöðuþáttum í tónfræði, öðlastu færni til að spila á píanó, lærðu nótnalestur og semdu
							þína eigin tónlist.
						</p>
						<form className='w-full' action='https://mussila.app.link'>
							<TertiaryButton grow type='submit'>
								Sækja Music
							</TertiaryButton>
						</form>
					</div>
					<div className='pb-4 pt-12 flex flex-col place-content-between place-items-center col-span-6 px-4 mx-2 text-lg p-16 text-gray-500'>
						<img src={WPTablet} alt='WordPlay Tablet' className='h-32 w-auto place-self-center object-contain' />
						<p className='flex flex-col px-8 max-w-md my-4 place-content-center place-items-center'>
							{/* Increase vocabulary, work on listening skills and improve reading comprehension. */}
							Bættu orðaforðann, æfðu virka hlustun, þjálfaðu lestur, máltjáningu og náðu betri tökum á lesskilningi og
							lestu og hlustaðu á sögur.
						</p>
						<form className='w-full' action='https://mussila-wordplay.app.link'>
							<TertiaryButton grow type='submit'>
								Sækja WordPlay
							</TertiaryButton>
						</form>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className='flex flex-col min-h-screen h-full place-content-start md:place-content-center place-items-center bg-dark-blue-400 py-4 pb-16'>
				<Snowfall style={{ height: '100%' }} />
				{isLoaded && (
					<a
						href='https://mussila.com'
						aria-label='Back to homepage'
						className={`hidden md:block mussila-logo font-blanch text-5xl sm:text-9xl pb-2 mx-12 place-self-start`}
					>
						mussila
					</a>
				)}
				<h1 className='invisible text-transparent select-none'>Mussila Jóladagatalið</h1>
				<div className='xl:my-16'></div>
				<div className='flex flex-col h-full' style={{ width: screenWidth }}>
					<div className='relative'>
						{isLoaded === false && (
							// We'll conditionally render the loading overlay if the Unity
							// Application is not loaded.
							<>
								<div
									className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full flex flex-col`}
								>
									{/* Mussila Logo */}
									<div className={`mussila-logo font-blanch text-9xl pb-2 mx-12 place-self-center`}>mussila</div>
									{/* Progress bar */}
									<div className='w-2/4 bg-white rounded-lg h-10 ring-4 ring-gray-200 place-self-center relative'>
										<div className='bg-mint-300 h-10 rounded-lg' style={{ width: `${loadingPercentage}%` }}></div>
										<p className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-xl text-dark-blue-500'>
											{loadingPercentage}%
										</p>
									</div>
								</div>
								<div className='pb-20' />
							</>
						)}
						<Unity
							className='unity bg-dark-blue-500 rounded-smd ring-2 ring-gray-100 relative z-50'
							unityProvider={unityProvider}
							style={{
								width: screenWidth,
								height: screenHeight,
								visibility: isLoaded ? 'visible' : 'hidden',
							}}
							devicePixelRatio={devicePixelRatio}
						/>
					</div>
					{isLoaded && (
						<>
						<img
							src={Krans}
							alt='background'
							className='hidden xl:block absolute top-0 left-0 w-full h-full z-0 select-none pointer-events-none'
						/>
						<div className='flex flex-row gap-2 my-4 place-content-between w-full relative z-50'>
							<SocialShareButtons url='https://app.mussila.com/dagatal' />
							<button
								type={'button'}
								disabled={false}
								onClick={() => requestFullscreen(true)}
								className={`group my-4 h-16 w-16 rounded-smd flex place-content-center place-items-center font-bold bg-dark-blue-600 ring-2 ring-gray-100 relative`}
							>
								{
									<ArrowsPointingOutIcon className='group-hover:w-10 group-hover:h-10 transform duration-150 w-8 h-8 text-white' />
								}
							</button>
						</div>
					</>
					)}
					<div className='flex flex-col sm:grid grid-cols-12 rounded-smd bg-white border-2 border-gray-100 w-full mt-16 place-content-center'>
						<div className='col-span-12 flex flex-col sm:flex-row place-content-between place-items-center bg-dark-blue-500 rounded-t-smd text-white p-4'>
							<div className='flex flex-col sm:flex-row grow place-content-start place-items-center sm:w-3/5 gap-4'>
								<img src={GiftWithBell} alt='Gift' className='w-32 h-auto px-4' />
								<div className='sm:ml-8 flex flex-col place-content-start place-items-start'>
									<h2 className='text-xl 2xl:text-2xl text-left uppercase font-extralight'>
										Gefðu <span className='font-blanch mussila-logo text-3xl 2xl:text-4xl leading-none'>mussila</span>{' '}
										um jólin
									</h2>
									<p className='text-base'>
										Eitt ár af Mussila á <span className='font-bold text-orange-400'>30%</span> afslætti
									</p>
									<p className='text-base'>
										með kóðanum <span className='font-bold text-orange-400 text-base'>DAGATAL30</span> við greiðslu
									</p>
									<p className='text-xs text-orange-400'>Tilboð stendur út 25 Desember.</p>
								</div>
							</div>
							<div className='sm:w-2/5 mr-4'>
								<a href='/giftcard/give'>
									<PrimaryButton grow>Gefðu gjöf</PrimaryButton>
								</a>
							</div>
						</div>
						<div className='pb-4 pt-12 flex flex-col place-content-between place-items-center col-span-6 px-4 mx-2 text-lg p-16 text-gray-500'>
							<img src={MusicTablet} alt='Music Tablet' className='h-32 w-auto place-self-center' />
							<p className='flex flex-col px-8 max-w-md my-4 place-content-center place-items-center'>
								{/* Learn to play the piano, create your own compositions and master the fundamentals of music theory. */}
								Náðu tökum á undirstöðuþáttum í tónfræði, öðlastu færni til að spila á píanó, lærðu nótnalestur og semdu
								þína eigin tónlist.
							</p>
							<form className='w-full' action='https://mussila.app.link'>
								<TertiaryButton grow type='submit'>
									Sækja Music
								</TertiaryButton>
							</form>
						</div>
						<div className='pb-4 pt-12 flex flex-col place-content-between place-items-center col-span-6 px-4 mx-2 text-lg p-16 text-gray-500'>
							<img src={WPTablet} alt='WordPlay Tablet' className='h-32 w-auto place-self-center object-contain' />
							<p className='flex flex-col px-8 max-w-md my-4 place-content-center place-items-center'>
								{/* Increase vocabulary, work on listening skills and improve reading comprehension. */}
								Bættu orðaforðann, æfðu virka hlustun, þjálfaðu lestur, máltjáningu og náðu betri tökum á lesskilningi
								og lestu og hlustaðu á sögur.
							</p>
							<form className='w-full' action='https://mussila-wordplay.app.link'>
								<TertiaryButton grow type='submit'>
									Sækja WordPlay
								</TertiaryButton>
							</form>
						</div>
					</div>
					{/* <div className='flex flex-col place-content-center place-items-center'>
						<div className='text-3xl xl:text-7xl font-balloon text-white mt-8'>
							Want to give someone 1 year of{' '}
							<span className='font-blanch mussila-logo text-7xl leading-none'>mussila</span>
						</div>
						<PrimaryButton>Purchase $47.99</PrimaryButton>
					</div> */}
				</div>
			</div>
		</>
	);
};

export default Calendar;
